import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Users, Clock, ArrowRight, Star, Ticket } from 'lucide-react';
import { getShows } from '../services/cmsData';

export default function ShowsPage() {
  const [shows, setShows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    const fetchShows = async () => {
      setLoading(true);
      try {
        const data = await getShows({ status: filter === 'all' ? null : filter });
        setShows(data);
      } catch (error) {
        console.error('Error fetching shows:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchShows();
  }, [filter]);

  return (
    <div data-testid="shows-page" className="min-h-screen">
      {/* Hero - Light Background */}
      <section className="relative pt-32 pb-20 px-6 bg-gradient-to-b from-gray-50 to-white overflow-hidden">
        <div className="max-w-7xl mx-auto relative">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-4xl mx-auto"
          >
            <p className="font-accent text-brand-primary text-sm tracking-[0.3em] mb-6 uppercase">
              Our Productions
            </p>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold text-brand-dark mb-8 leading-tight">
              Spectacular<br />
              <span className="text-brand-primary">Family Shows</span>
            </h1>
            <p className="font-body text-gray-600 text-xl max-w-2xl mx-auto">
              From beloved children's characters to educational adventures, discover our range of incredible live productions touring the UK
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filters */}
      <section className="py-8 bg-white sticky top-16 z-30 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-wrap justify-center gap-4">
            {[
              { value: 'all', label: 'All Shows' },
              { value: 'current', label: 'Touring' },
              { value: 'available', label: 'Available' },
              { value: 'pantomime', label: 'Pantomime' },
              { value: 'coming-soon', label: 'Coming Soon' },
            ].map((option) => (
              <button
                key={option.value}
                onClick={() => setFilter(option.value)}
                data-testid={`filter-${option.value}`}
                className={`px-8 py-4 rounded-full font-body text-base font-semibold transition-all duration-300 ${
                  filter === option.value
                    ? 'bg-brand-primary text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Shows Grid - BIGGER CARDS */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-10">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="aspect-[3/4] bg-gray-200 animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-10">
              {shows.map((show, index) => (
                <motion.div
                  key={show.id}
                  initial={{ opacity: 0, y: 40 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link
                    to={`/shows/${show.id}`}
                    data-testid={`show-card-${show.id}`}
                    className="block group"
                  >
                    <div className="relative aspect-[3/4] overflow-hidden shadow-xl transition-all duration-500 group-hover:shadow-[0_30px_70px_rgba(8,6,94,0.25)] group-hover:-translate-y-4">
                      {/* Image */}
                      <img
                        src={`${process.env.PUBLIC_URL}${show.poster}`}
                        alt={show.title}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      />
                      
                      {/* Gradient Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-brand-dark via-brand-dark/30 to-transparent opacity-80 group-hover:opacity-90 transition-opacity" />
                      
                      {/* Badges */}
                      <div className="absolute top-6 left-6 right-6 flex justify-between items-start">
                        <span className={`px-4 py-2 rounded-full text-sm font-bold ${
                          show.status === 'current'
                            ? 'bg-green-500 text-white'
                            : show.status === 'available'
                            ? 'bg-blue-500 text-white'
                            : show.status === 'pantomime'
                            ? 'bg-purple-500 text-white'
                            : show.status === 'coming-soon'
                            ? 'bg-accent-gold text-brand-dark'
                            : 'bg-gray-500/80 text-white'
                        }`}>
                          {show.status === 'current' ? 'Now Touring' : show.status === 'available' ? 'Available' : show.status === 'pantomime' ? 'Pantomime' : show.status === 'coming-soon' ? 'Coming Soon' : 'Past Show'}
                        </span>
                        {show.featured && (
                          <span className="flex items-center gap-2 px-4 py-2 rounded-full bg-accent-gold text-brand-dark text-sm font-bold shadow-lg">
                            <Star className="w-4 h-4" fill="currentColor" />
                            Featured
                          </span>
                        )}
                      </div>
                      
                      {/* Content */}
                      <div className="absolute bottom-0 left-0 right-0 p-8">
                        <h3 className="font-display text-3xl font-bold text-white mb-3 group-hover:text-accent-gold transition-colors duration-300">
                          {show.shortTitle}
                        </h3>
                        <p className="font-body text-white/80 text-lg mb-5 line-clamp-2">
                          {show.tagline}
                        </p>
                        {(show.ageRange || show.duration) && (
                          <div className="flex items-center gap-6 text-base text-white/70 mb-6">
                            {show.ageRange && (
                              <span className="flex items-center gap-2">
                                <Users className="w-5 h-5" />
                                Ages {show.ageRange}
                              </span>
                            )}
                            {show.duration && (
                              <span className="flex items-center gap-2">
                                <Clock className="w-5 h-5" />
                                {show.duration}
                              </span>
                            )}
                          </div>
                        )}
                        
                        {/* Hover CTA */}
                        <div className="flex gap-3 flex-nowrap transform translate-y-4 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300">
                          <span className="inline-flex items-center gap-2 bg-accent-gold text-brand-dark px-5 py-2.5 rounded-full font-bold text-sm whitespace-nowrap">
                            {show.status === 'coming-soon' ? 'More Info' : 'View Details'}
                            <ArrowRight className="w-4 h-4" />
                          </span>
                          {show.status !== 'coming-soon' && (
                            <span className="inline-flex items-center gap-2 bg-white/20 backdrop-blur text-white px-5 py-2.5 rounded-full font-semibold text-sm whitespace-nowrap">
                              <Ticket className="w-4 h-4" />
                              Get Tickets
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}

          {!loading && shows.length === 0 && (
            <div className="text-center py-20">
              <p className="font-body text-gray-500 text-xl">
                No shows found for this filter.
              </p>
              <button
                onClick={() => setFilter('all')}
                className="mt-6 px-8 py-4 bg-brand-primary text-white rounded-full font-semibold hover:bg-brand-dark transition-colors"
              >
                View All Shows
              </button>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-brand-primary">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-white mb-6">
            Can't Decide? Find a Show Near You!
          </h2>
          <p className="font-body text-white/80 text-lg mb-8">
            Search by location to find the perfect family day out
          </p>
          <Link
            to="/tour-dates"
            data-testid="shows-find-dates-btn"
            className="inline-flex items-center gap-3 bg-white text-brand-primary px-10 py-5 rounded-full font-bold text-lg transition-all duration-300 hover:bg-accent-gold hover:text-brand-dark hover:scale-105"
          >
            Browse Tour Dates
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
}
